import { ReactNode, useCallback, useEffect, useReducer } from "react";
import { ThemeProvider } from "@material-ui/core/styles";
import NavigationMenus from "../NavigationMenus";
import * as ROUTES from "@/constants/route";
import {
  getCurrentKycReview,
  submitKycReviewUpdate,
} from "@/services/kycReview";
import useNotify from "@/hooks/useNotify";
import { KycAttachmentMeta, parseKycReviewCurrent } from "@/types/kycReview";
import {
  COPY,
  STEP,
  STEP_SUBTITLES,
  isTier3Variant,
  stepLabel,
  visibleSteps,
} from "./constants";
import { buildSubmitPayload } from "./logic/payload";
import {
  getAttachmentUploadErrorMessage,
  uploadAccountUpdateAttachment,
} from "./logic/uploadAttachment";
import {
  findFirstInvalidStep,
  validateStep,
  ValidationContext,
} from "./logic/validation";
import { fromSnapshot, isLoadableSnapshot } from "./state/fromSnapshot";
import { initialState, reducer } from "./state/reducer";
import { kycTheme, rv } from "./tokens";
import ActionBar from "./ui/ActionBar";
import StepShell from "./ui/StepShell";
import StepTracker from "./ui/StepTracker";
import { makeStyles } from "@material-ui/core/styles";
import Loading from "./phases/Loading";
import ErrorState from "./phases/ErrorState";
import PendingBlock from "./phases/PendingBlock";
import Submitted from "./phases/Submitted";
import AddressDetails from "./steps/AddressDetails";
import ClientInformation from "./steps/ClientInformation";
import Fatca from "./steps/Fatca";
import PEP from "./steps/PEP";
import Professional from "./steps/Professional";
import Review from "./steps/Review";
import RiskStep from "./steps/RiskStep";
import Settlement from "./steps/Settlement";
import { StepHandlers, StepViewProps } from "./steps/types";

const usePageStyles = makeStyles((theme) => ({
  page: {
    margin: theme.spacing(-3),
    background: rv.contentBg,
    minHeight: "calc(100vh - 64px)",
  },
  inner: {
    maxWidth: 980,
    margin: "0 auto",
    padding: theme.spacing(4, 4, 8),
    "@media (max-width: 760px)": { padding: theme.spacing(3, 2, 14) },
  },
}));

interface WizardProps {
  history: { push: (path: string) => void };
}

function scrollToFirstError(): void {
  window.setTimeout(() => {
    const el = document.querySelector<HTMLElement>("[data-rv-error]");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  }, 30);
}

function errorStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

function errorMessage(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { message?: string } }; message?: string })
      ?.response?.data?.message ||
    (err as { message?: string })?.message ||
    fallback
  );
}

export default function UpdateKycWizard({ history }: WizardProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const notify = useNotify();
  const pageClasses = usePageStyles();

  const loadData = useCallback(() => {
    let active = true;
    getCurrentKycReview()
      .then((response) => {
        if (!active) return;
        const parsed = parseKycReviewCurrent(response.data?.data);
        if (!isLoadableSnapshot(parsed)) {
          dispatch({ type: "LOAD_ERROR", message: COPY.error.loadGeneric });
          return;
        }
        dispatch({ type: "LOAD_SUCCESS", snapshot: fromSnapshot(parsed) });
      })
      .catch((err) => {
        if (!active) return;
        dispatch({
          type: "LOAD_ERROR",
          message: errorMessage(err, COPY.error.loadGeneric),
        });
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => loadData(), [loadData]);

  const isTier3 = isTier3Variant(state.tier, state.formVariant);
  const visible = visibleSteps(isTier3);
  const position = visible.indexOf(state.stepIndex);
  const isUploading = Object.values(state.uploads).some(
    (entry) => entry.status === "uploading"
  );

  const ctx: ValidationContext = {
    draft: state.draft,
    originalKyc: state.originalKyc,
    uploads: state.uploads,
    marriageNameMatchesId: state.marriageNameMatchesId,
    confirmed: state.confirmed,
    isTier3,
    riskDirty: state.risk.dirty,
    sameAsPresentAddress: state.sameAsPresentAddress,
  };

  const goToStep = (index: number) => {
    dispatch({ type: "GO_TO_STEP", index });
    window.scrollTo(0, 0);
  };

  const next = () => {
    const errors = validateStep(state.stepIndex, ctx);
    if (Object.keys(errors).length) {
      dispatch({ type: "SET_ERRORS", errors });
      scrollToFirstError();
      return;
    }
    const nextIndex = visible[position + 1];
    if (nextIndex !== undefined) goToStep(nextIndex);
  };

  const back = () => {
    const prevIndex = visible[position - 1];
    if (prevIndex !== undefined) goToStep(prevIndex);
  };

  const uploadFile = async (image: string, file: File) => {
    dispatch({ type: "SET_UPLOAD", image, entry: { status: "uploading", name: file.name } });
    try {
      const meta = await uploadAccountUpdateAttachment({ image, file });
      dispatch({
        type: "SET_UPLOAD",
        image,
        entry: { status: "uploaded", name: meta.fileName || file.name, meta },
      });
    } catch (err) {
      dispatch({
        type: "SET_UPLOAD",
        image,
        entry: {
          status: "empty",
          error: getAttachmentUploadErrorMessage({
            error: err,
            fallback: `We could not upload ${file.name}. Please try again.`,
          }),
        },
      });
    }
  };

  const submit = async () => {
    const invalid = findFirstInvalidStep(ctx, visible);
    if (invalid) {
      dispatch({ type: "GO_TO_STEP", index: invalid.step });
      dispatch({ type: "SET_ERRORS", errors: invalid.errors });
      notify(COPY.error.blockedSubmit, "warning");
      scrollToFirstError();
      return;
    }

    dispatch({ type: "SUBMIT_START" });
    const attachments: KycAttachmentMeta[] = Object.values(state.uploads)
      .filter((entry) => entry.status === "uploaded" && entry.meta)
      .map((entry) => entry.meta as KycAttachmentMeta);

    try {
      await submitKycReviewUpdate(
        buildSubmitPayload({
          draft: state.draft,
          isTier3,
          riskDirty: state.risk.dirty,
          attachments,
        })
      );
      dispatch({ type: "SUBMIT_SUCCESS" });
      window.scrollTo(0, 0);
    } catch (err) {
      if (errorStatus(err) === 409) {
        dispatch({ type: "SUBMIT_PENDING" });
        return;
      }
      const message = errorMessage(err, COPY.error.generic);
      dispatch({ type: "SUBMIT_ERROR", message });
      notify(message, "error");
    }
  };

  const handlers: StepHandlers = {
    setUserField: (path, value) => dispatch({ type: "SET_USER_FIELD", path, value }),
    setKycField: (path, value) => dispatch({ type: "SET_KYC_FIELD", path, value }),
    setAccountField: (path, value) => dispatch({ type: "SET_ACCOUNT_FIELD", path, value }),
    answerRisk: (question, selected) =>
      dispatch({ type: "SET_RISK_ANSWER", question, selected }),
    setConfirmed: (value) => dispatch({ type: "SET_CONFIRMED", value }),
    setSameAsPresent: (value) => dispatch({ type: "SET_SAME_AS_PRESENT", value }),
    setMarriageNameMatchesId: (value) =>
      dispatch({ type: "SET_MARRIAGE_NAME_MATCHES_ID", value }),
    uploadFile,
    removeUpload: (image) => dispatch({ type: "REMOVE_UPLOAD", image }),
    goToStep,
  };

  const stepProps: StepViewProps = { state, isTier3, handlers };

  const renderStep = (): ReactNode => {
    switch (state.stepIndex) {
      case STEP.CLIENT:
        return <ClientInformation {...stepProps} />;
      case STEP.ADDRESS:
        return <AddressDetails {...stepProps} />;
      case STEP.PROFESSIONAL:
        return <Professional {...stepProps} />;
      case STEP.PEP:
        return <PEP {...stepProps} />;
      case STEP.FATCA:
        return <Fatca {...stepProps} />;
      case STEP.RISK:
        return <RiskStep {...stepProps} />;
      case STEP.SETTLEMENT:
        return <Settlement {...stepProps} />;
      default:
        return <Review {...stepProps} />;
    }
  };

  const tierKnown = state.tier !== null;
  const sidebar = tierKnown ? (
    <StepTracker
      steps={visible.map((index) => ({ index, label: stepLabel(index, !isTier3) }))}
      currentIndex={state.stepIndex}
      onJump={goToStep}
    />
  ) : undefined;

  const returnToDashboard = () => history.push(ROUTES.DASHBOARD);
  const upgradeTier3 = () => history.push(ROUTES.LONG_KYC_LAST);

  let content: ReactNode;
  if (state.phase === "loading") {
    content = <Loading />;
  } else if (state.phase === "error") {
    content = (
      <ErrorState
        message={state.loadError || COPY.error.loadGeneric}
        onRetry={() => {
          dispatch({ type: "RETRY_LOAD" });
          loadData();
        }}
      />
    );
  } else if (state.phase === "pending") {
    content = <PendingBlock onReturnDashboard={returnToDashboard} />;
  } else if (state.phase === "submitted") {
    content = (
      <Submitted
        isTier2={!isTier3}
        onReturnDashboard={returnToDashboard}
        onUpgrade={upgradeTier3}
      />
    );
  } else {
    const isReview = state.stepIndex === STEP.REVIEW;
    content = (
      <StepShell
        title={stepLabel(state.stepIndex, !isTier3)}
        subtitle={STEP_SUBTITLES[state.stepIndex]}
        stepText={`Step ${position + 1} of ${visible.length}`}
        tierLabel={isTier3 ? "Tier 3" : "Tier 2"}
        serverError={isReview ? state.serverError : undefined}
        actionBar={
          <ActionBar
            isReview={isReview}
            canBack={position > 0}
            busy={state.phase === "submitting" || isUploading}
            submitting={state.phase === "submitting"}
            confirmed={state.confirmed}
            onBack={back}
            onContinue={next}
            onSubmit={submit}
          />
        }
      >
        {renderStep()}
      </StepShell>
    );
  }

  return (
    <NavigationMenus
      sidebarContent={sidebar}
      content={
        <ThemeProvider theme={kycTheme}>
          <div className={pageClasses.page}>
            <div className={pageClasses.inner}>{content}</div>
          </div>
        </ThemeProvider>
      }
    />
  );
}
